import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { stage2Service } from 'services/stage2Service';
import { parseApiError } from 'utils/errorParser';
import { notify } from 'features/ui/uiSlice';
import { MESSAGES } from 'constants/messages';

export const fetchStage2 = createAsyncThunk('stage2/fetch', async (params, { rejectWithValue }) => {
  try {
    return await stage2Service.list(params);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const routeArticle = createAsyncThunk('stage2/route', async ({ id, decision, notes, documentVersion }, { dispatch, rejectWithValue }) => {
  try {
    const updated = await stage2Service.route(id, { decision, notes, documentVersion });
    dispatch(notify({ type: 'success', message: MESSAGES.PIPELINE.ROUTED }));
    return updated;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

export const bulkRouteArticles = createAsyncThunk('stage2/bulkRoute', async ({ items, decision, notes }, { dispatch, rejectWithValue }) => {
  try {
    const results = await Promise.allSettled(
      items.map(item => stage2Service.route(item.id, { decision, notes, documentVersion: item.documentVersion }))
    );
    const successes = results.filter(r => r.status === 'fulfilled');
    const failures = results.filter(r => r.status === 'rejected');
    
    if (successes.length > 0) {
      dispatch(notify({ type: 'success', message: `Successfully routed ${successes.length} articles.` }));
    }
    if (failures.length > 0) {
      dispatch(notify({ type: 'error', message: `Failed to route ${failures.length} articles.` }));
    }
    
    return successes.map(r => r.value);
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});


const upsert = (state, item) => {
  const idx = state.items.findIndex((i) => i.id === item.id);
  if (idx !== -1) state.items[idx] = item;
};

const stage2Slice = createSlice({
  name: 'stage2',
  initialState: {
    items: [],
    meta: { total: 0, page: 1, pageSize: 12, totalPages: 1 },
    status: 'idle',
    saving: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStage2.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchStage2.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.meta = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchStage2.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload?.message; })
      .addCase(routeArticle.pending, (state) => { state.saving = true; })
      .addCase(routeArticle.fulfilled, (state, action) => { state.saving = false; upsert(state, action.payload); })
      .addCase(routeArticle.rejected, (state) => { state.saving = false; })
      .addCase(bulkRouteArticles.pending, (state) => { state.saving = true; })
      .addCase(bulkRouteArticles.fulfilled, (state, action) => {
        state.saving = false;
        action.payload.forEach(item => upsert(state, item));
      })
      .addCase(bulkRouteArticles.rejected, (state) => { state.saving = false; });
  },
});

export default stage2Slice.reducer;
